/* =====================================================================
   sts-add-routine.js — "Add to my routine" (prototype v6 6.2 / 6.3, PDP-1,
   PDP-2, decision d). Loaded by snippets/sts-add-routine.liquid on the product
   page, the collection / search grids and (v6.1 1.5b) the homepage rows, brand
   pages, Deals, the Skin Profile upsells and the article callout.

   PDP: [data-sts-rt-add] (v7.1 §2: the buy box's text link under the
   payment buttons, shown pre-paint by the .sts-if-ok rules; the sticky bar no
   longer has one) reads "+ Add to my routine", or "✓ In your routine" once the
   product is in it (a click then opens the routine widget). A smaller size
   asks first, as Add to cart does (assets/sts-small-size.js). Cards: approved
   cards get "+ Add to my routine" under Add to cart.

   Flow: fill an empty matching step; a filled one asks "Swap this step" /
   "Have both in my routine" (the Replace popup); a product that fits no step
   goes to Extra steps (prototype v6.2 addToRoutine: no slot of the routine ->
   extras {key:'addon'}, "Now in Extra steps."). A step fits when it carries
   one of the product's slot codes, as the server matches (stepsForSlots), so
   a C01 wash is an extra step in a C02 routine, never "not a step" (Tyler,
   2026-10-14). Signed in with saving on: POST {proxy}/routine/add
   {variantId, time, mode fill|swap|both|extra, scope} (409 STEP_FILLED -> the
   popup; 422 NO_MATCHING_STEP -> again with mode extra). Otherwise the same rules run on the local routine: a guest's
   slqSavedRegimen (+ extras[]), or a visit-only copy (sessionStorage
   'sts.addrt') for a signed-in customer while saving is off and for the
   staff simulator. Then: "Your routine changed" (STS.routineChanged; for a
   subscriber it becomes the sts:routine:changed consent event), the PDP
   confirmation card with See my routine / Undo, and the widget.

   HOOKS (other agents' APIs, all feature-checked):
     STS.routineWidget.open({changedStepIds, badge, steps}) / refresh(steps)
       (#592): this file hands the widget the routine it changed (steps +
       extras flagged extra:true), since a visit-only or extra step lives
       here; the widget badges, scrolls to and glows the changed rows.
     STS.swapBoth.ask({current, next, places, time}) -> Promise 'all' | 'one'
       | null (#593, assets/sts-popups.js; v6 3.5); absent = the step only.
     STS.routineHistory.record('pdp_swap' | 'pdp_add') after a change, where
       the history exists (#593 builds it on the Skin Profile only). It has no
       restore-by-entry call, so Undo reverses the change just made itself.
   PUBLISHED: STS.addRoutine = {get() -> Promise<{steps, extras}|null>,
     add(product, button), inRoutine(product), undo(), paint(),
     removeExtra(extraId) -> Promise<bool> (the widget's ×),
     match(steps, slots) -> steps, stepWords(step, shared) -> 'morning … step'};
     document event sts:routine:updated {steps, extras, changedStepIds, kind}
     (kind 'restore': this visit's saved change, read back after a reload).
   LISTENS: sts:routine:reverted {doc} (#599 Keep), sts:routine:change {steps}.
   ===================================================================== */
(function (w, d) {
  'use strict';
  var STS = (w.STS = w.STS || {});
  if (STS.addRoutine) return;
  var CFG = STS.addRoutineCfg || {};
  var VISIT = 'sts.addrt', DEVICE = 'slqSavedRegimen';
  var FAM = { C: 'Cleanser', E: 'Eye cream', M: 'Moisturizer', S: 'Sun protection', SPF: 'Sun protection', T: 'Treatment', EX: 'Exfoliant', TN: 'Toner' };
  /* Travel sizes stay out of routines (Luis, 2026-09-26): the API answers 422
     not_retail for a variant title matching skin-type-pro's
     NON_RETAIL_VARIANT_TOKENS (lib/variant-exclusions.ts); same tokens here,
     so the PDP says so before anyone taps. */
  var TRAVEL = 'Travel sizes can’t be added to your routine. Pick a full size to add it.';
  var NONRETAIL = /sample|travel|tester|back ?bar|pro[ _-]?size|trial|mini|kit/i;
  var WHY = {
    malformed: 'We could not read that size.', unknown_variant: 'We do not have that size on file.',
    inactive: 'That size is no longer sold.', not_retail: TRAVEL,
    excluded_vendor: 'That brand cannot go in a routine.', out_of_stock: 'That size is out of stock.'
  };
  var cur = null, loading = null, last = null, busy = false;
  /* A signed-in customer whose account holds no routine: their type's default
     stands in and their changes last for the visit (load). */
  var fallback = 0;

  function qsa(r, s) { return [].slice.call((r || d).querySelectorAll(s)); }
  function mk(tag, cls, txt) { var n = d.createElement(tag); if (cls) n.className = cls; if (txt != null) n.textContent = txt; return n; }
  function parse(s) { try { return JSON.parse(s); } catch (e) { return null; } }
  function get(st, k) { try { return w[st].getItem(k); } catch (e) { return null; } }
  function put(st, k, v) { try { w[st].setItem(k, v); } catch (e) { /* blocked */ } }
  function str(v) { return v == null ? '' : String(v); }
  function vid(v) { var m = str(v).match(/(\d+)\s*$/); return m ? m[1] : str(v); }
  function emit(n, det) { try { d.dispatchEvent(new CustomEvent(n, { detail: det })); } catch (e) { /* old engine */ } }
  function state() { return STS.state || {}; }
  function type() { return str(state().type || d.documentElement.getAttribute('data-sts-type')).toUpperCase(); }
  function sim() { return !!(STS.preview && STS.preview.active) || !!(STS.isSimulated && STS.isSimulated()); }
  function owner() { return sim() ? 'sim' : 'c' + str((w.__STS_SERVER__ || {}).customerId); }
  function mode() { return sim() ? 'visit' : state().loggedIn ? (CFG.saving && !fallback ? 'server' : 'visit') : 'device'; }
  function env(k, f) { var E = STS.env; return str(E && E.get ? E.get(k, f) : f).replace(/\/+$/, ''); }
  function proxy() { return env('proxyBase', CFG.proxy || '/apps/quiz'); }
  function clone(r) {
    return { steps: (r.steps || []).map(function (s) { return Object.assign({}, s); }), extras: (r.extras || []).slice(),
      quantities: r.quantities || null, routineSize: r.routineSize || null, sensitivity: r.sensitivity || null };
  }
  function notice(t, b) { var P = STS.popups; if (P && P.notice) P.notice({ title: t, body: b }); }

  /* ---- the routine ----------------------------------------------------- */
  function json(url, o) {
    o = o || {};
    o.credentials = 'same-origin';
    o.headers = o.headers || { Accept: 'application/json' };
    return w.fetch(url, o).then(function (r) { return r.ok && r.status !== 204 ? r.json() : null; });
  }
  function remote() {
    var P = proxy();
    return (CFG.saving ? json(P + '/profile') : Promise.resolve(null)).then(function (doc) {
      if (doc && Array.isArray(doc.steps) && doc.steps.length) return doc;
      return json(P + '/my-regimen').then(function (m) {
        if (!m || !m.widgetToken) return null;
        return json(env('apiBase', 'https://api.skintype.pro') + '/widget/regimen', { headers: { Authorization: 'Bearer ' + m.widgetToken } });
      });
    }).catch(function () { return null; });
  }
  /* The Skin Profile (v6.1 1.5b, its upsell cards): the page's own editor
     holds the routine (STS.routine, sts-profile.js), removals included, so it
     is read fresh every time and nothing else is fetched there. */
  function live() {
    var R = STS.routine;
    var s = R && R.all && R.apply ? R.all() : null;
    return s && s.length ? { steps: s, extras: R.extras ? R.extras() : [] } : null;
  }
  function load() {
    var L = live();
    if (L) { cur = clone(L); return Promise.resolve(cur); }
    if (cur) return Promise.resolve(cur);
    if (loading) return loading;
    if (d.querySelector('[data-sts-profile]')) return Promise.resolve(null);
    var t = type();
    /* This visit's routine, the one every page reads (STS.visitRoutine, sts-state.js). */
    var V = STS.visitRoutine, ov = V ? V.get() : parse(get('sessionStorage', VISIT));
    if (ov && (V || (ov.t === t && ov.o === owner())) && Array.isArray(ov.steps)) {
      cur = clone(ov);
      cur.visit = 1;
      /* Signed in with saving on, a visit copy is the no-routine fallback's. */
      if (state().loggedIn && !sim()) fallback = 1;
      return Promise.resolve(cur);
    }
    var pv = STS.previewRoutine ? Promise.resolve(STS.previewRoutine()).catch(function () { return null; }) : Promise.resolve(null);
    loading = pv.then(function (r) {
      if (r && r.steps) return r;
      if (sim()) return null;
      /* The device copy is a guest's routine. A signed-in customer's is the
         account's, read as the Skin Profile reads it (remote); the results
         page's cache on this device is an older snapshot of it, and handing
         that to the routine widget made it disagree with the Skin Profile. */
      /* A known type always has a routine (assets/sts-type-routine.js): a
         guest's is settled on this device; an account with none gets its
         type's default, changed for the visit only (mode 'visit'), as while
         saving is off, since the server has no routine to add to. */
      var TR = STS.typeRoutine;
      if (state().loggedIn) {
        return remote().then(function (x) {
          if (x && Array.isArray(x.steps) && x.steps.length) return x;
          var b = TR && t ? TR.build(t) : null;
          if (b) b.fallback = 1;
          return b;
        });
      }
      if (TR) return TR.guest(t);
      var c = parse(get('localStorage', DEVICE));
      if (c && Array.isArray(c.steps) && c.steps.length && str(c.skinType).toUpperCase() === t &&
        (!c.cachedAt || Date.now() - c.cachedAt < 2592e6)) return c;
      return null;
    }).then(mkt).then(function (r) {
      loading = null;
      cur = r && Array.isArray(r.steps) ? clone(r) : null;
      if (cur && r.fallback) fallback = 1;
      return cur;
    }, function () { loading = null; return null; });
    return loading;
  }
  /* v6.1 13.3: a routine read from the backend or the device carries US
     prices; outside the shop's currency its steps and extras are repriced from
     the market's own price list (STS.money.reprice). At home nothing changes. */
  function mkt(r) {
    var M = STS.money, F = STS.stepFallback, P = F && F.prices;
    /* QA 2026-10-01: the live store price, at home too (sts-step-fallback.js). */
    if (!r || !P && (!M || !M.reprice || M.code() === M.shop)) return r;
    P = P || M.reprice;
    return Promise.all([P(r.steps), P(r.extras)]).then(function (x) { r.steps = x[0]; if (r.extras) r.extras = x[1]; return r; });
  }
  /* Where a change lives: the device routine for a guest, the visit for a
     signed-in customer while saving is off (and the simulator), and for a
     saved change the device copy follows the server's document. */
  function save(r) {
    cur = r;
    var m = mode();
    if (m === 'visit') {
      if (STS.visitRoutine) STS.visitRoutine.save(r);
      else put('sessionStorage', VISIT, JSON.stringify({ t: type(), o: owner(), steps: r.steps, extras: r.extras }));
      return;
    }
    var c = parse(get('localStorage', DEVICE));
    if (m === 'server' && !c) return;
    c = c || { skinType: type(), cachedAt: Date.now() };
    c.steps = r.steps;
    c.extras = r.extras;
    put('localStorage', DEVICE, JSON.stringify(c));
  }

  /* ---- matching (the server's rule, then looser for a local routine) ---- */
  function tod(s) { var f = STS.routineTime; var t = f ? f(s.time != null ? s.time : s.period) : str(s.time).toUpperCase(); return t === 'PM' ? 'PM' : 'AM'; }
  function empty(s) { return s.included === false || !s.dtcVariantId; }
  function key(s) { return s.stepId || 'local:' + tod(s) + ':' + str(s.slot); }
  /* ECS, the regimen templates' eye placeholder, is family E and stands for
     any E slot (STS.slotFam / sameSlot, sts-state.js): an unresolved eye step
     takes an eye cream exactly as a resolved one would. */
  function fam(c) { c = str(c).toUpperCase(); return /^HA/.test(c) ? c : STS.slotFam ? STS.slotFam(c) : c.replace(/[0-9].*$/, ''); }
  function base(c) { return str(c).toUpperCase().replace(/(\d)T$/, '$1'); }
  function slotEq(a, b) { return STS.sameSlot ? STS.sameSlot(a, b) : a === b; }
  /* Exact code, then without a tinted T (S25T -> S25). The family (any
     cleanser for any cleanser) is not a match: the server never fills or
     swaps across codes, so a family hit told a signed-in shopper "Not a step
     in your routine" and a guest got a swap the account would refuse. */
  function matches(steps, slots, loose) {
    var tiers = loose ? [function (x) { return x; }, base, fam] : [function (x) { return x; }, base];
    for (var i = 0; i < tiers.length; i++) {
      var want = slots.map(tiers[i]);
      var hit = steps.filter(function (s) {
        var k = s.slot && tiers[i](str(s.slot).toUpperCase());
        return k && want.some(function (x) { return slotEq(x, k); });
      });
      if (hit.length) return hit;
    }
    return [];
  }
  /* v6.1 §5.1: a variant is its own routine item. A step that names a variant
     matches only that variant; the handle is the fallback for one that names
     none. */
  function same(a, p) {
    if (!a || !p) return false;
    var v = vid(a.dtcVariantId || a.variantId);
    return v ? p.variants.indexOf(v) > -1 : !!(a.handle && a.handle === p.handle);
  }
  function inRoutine(p, r) {
    r = r || cur;
    return !!r && (r.steps.some(function (s) { return !empty(s) && same(s, p); }) || r.extras.some(function (e) { return same(e, p); }));
  }
  function slotName(slot) {
    var map = (d.querySelector('[data-sts-slot-names]') || { getAttribute: function () { return ''; } }).getAttribute('data-sts-slot-names') || '';
    var k = str(slot).toUpperCase(), hit = '';
    map.split('|').forEach(function (pair) { var i = pair.indexOf('='); if (i > 0 && pair.slice(0, i).trim().toUpperCase() === k) hit = pair.slice(i + 1).trim(); });
    return hit;
  }
  /* Never "Eye Cream Specific": STS.stepLabel drops the placeholder. */
  function label(s) { return str((STS.stepLabel ? STS.stepLabel(s) : s.label || s.category) || slotName(s.slot) || FAM[fam(s.slot)] || 'routine'); }
  function stepWords(s, shared) {
    return (tod(s) === 'PM' ? 'evening ' : 'morning ') + label(s).toLowerCase() + ' step' + (shared ? ' (AM + PM)' : '');
  }
  function asStep(p, s) {
    return Object.assign({}, s, {
      dtcVariantId: p.variantId, productId: p.productId, handle: p.handle, url: '/products/' + p.handle,
      title: p.title, displayTitle: p.displayTitle || p.title, variantTitle: p.variantTitle || null,
      brand: p.brand, priceMsrp: p.price, image: p.image, inStock: true, included: true
    });
  }
  function extraOf(p, paired, time) {
    return {
      extraId: 'extra:local-' + p.variantId, time: time || null, slot: paired ? paired.slot : (p.slots[0] || ''),
      label: paired ? label(paired) : '', pairedStepId: paired ? key(paired) : null, dtcVariantId: p.variantId,
      productId: p.productId, handle: p.handle, title: p.title, displayTitle: p.displayTitle || p.title,
      variantTitle: p.variantTitle || null, brand: p.brand, image: p.image, priceMsrp: p.price,
      addedAt: new Date().toISOString()
    };
  }
  /* For STS.routineChanged: steps + extras in its step shape. */
  function flat(r) {
    if (!r) return [];
    return r.steps.map(function (s) {
      var o = Object.assign({}, s, { stepId: key(s), time: tod(s), label: label(s) });
      if (empty(s)) { o.dtcVariantId = null; o.productId = null; o.title = null; o.displayTitle = null; o.brand = null; o.priceMsrp = null; }
      return o;
    }).concat(r.extras.map(function (e) {
      return { extra: true, key: e.extraId, extraId: e.extraId, stepId: e.extraId, time: e.time, slot: e.slot, dtcVariantId: e.dtcVariantId, title: e.title, displayTitle: e.displayTitle || e.title, brand: e.brand, priceMsrp: e.priceMsrp, image: e.image, handle: e.handle, label: e.label ? label(e) : '' };
    }));
  }

  /* ---- the Replace popup (v6 4.3; PDP-1) -------------------------------- */
  function row(tag, cls, name, img, price) {
    var r = mk('div', 'sts-rp__row' + cls);
    var pic = mk(img ? 'img' : 'span', 'sts-rp__img');
    if (img) { pic.src = img; pic.alt = name || ''; pic.width = 44; pic.height = 44; }
    r.appendChild(pic);
    var t = mk('div', 'sts-rp__text');
    t.appendChild(mk('p', 'sts-rp__tag', tag));
    t.appendChild(mk('p', 'sts-rp__name', name));
    r.appendChild(t);
    r.appendChild(mk('span', 'sts-rp__price', price));
    return r;
  }
  /* v6.1 13.3: the head's STS.money, the market's own format. */
  function money(n) {
    if (n == null || !isFinite(n)) return '';
    return STS.money ? STS.money.format(n) : '$' + Number(n).toFixed(2);
  }
  /* The variant's name ("Title (Variant)", #1055 displayTitle) wherever a routine product is named. */
  function nm(x) { var t = x.displayTitle || x.title; return STS.productName ? STS.productName(x.brand, t) : [x.brand, t].filter(Boolean).join(' '); }
  function askReplace(step, curP, newP, shared) {
    return new Promise(function (resolve) {
      var P = STS.popups;
      if (!P || !P.enqueue) { resolve(null); return; }
      P.enqueue({
        id: 'sts-rt-replace',
        open: function (done) {
          var box = mk('div', 'sts-rp__rows');
          box.appendChild(row('IN YOUR ROUTINE NOW', '', nm(curP), curP.image, money(curP.price != null ? curP.price : curP.priceMsrp)));
          box.appendChild(row('NEW', ' sts-rp__row--new', nm(newP), newP.image, money(newP.price)));
          P.dialog({
            id: 'sts-rt-replace', width: 460, row: true,
            /* v6.1 1.3: the title says it; the two rows show which step. */
            eyebrow: 'Your routine', title: 'You already have this step',
            content: box,
            buttons: [{ label: 'Swap this step', value: 'swap' }, { label: 'Have both in my routine', value: 'both', outline: true }],
            onClose: function (v) { done(); resolve(v || null); }
          });
        }
      });
    });
  }
  /* v6 3.5 through the routine editor's popup when it is there. */
  function askScope(curP, newP, occ, time) {
    var S = STS.swapBoth;
    if (!occ || occ.length < 2 || !S || typeof S.ask !== 'function') return Promise.resolve('step');
    var places = occ.map(function (o) { return { time: o.time, label: label(o) }; });
    return Promise.resolve(S.ask({ current: nm(curP), next: nm(newP), places: places, time: time === 'PM' ? 'PM' : 'AM' }))
      .then(function (v) { return v === 'all' ? 'all' : v === 'one' || v === 'step' ? 'step' : null; }, function () { return null; });
  }

  /* ---- the add ---------------------------------------------------------- */
  /* nonce=<uuid> on every write: the other_time retry comes at once, and one
     signature twice is a 401 replay (skin-type-pro #1091). */
  function nonce() {
    try { if (w.crypto && w.crypto.randomUUID) return w.crypto.randomUUID(); } catch (e) { /* fall through */ }
    var s = '', i;
    for (i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16);
    return s.slice(0, 8) + '-' + s.slice(8, 12) + '-4' + s.slice(13, 16) + '-a' + s.slice(17, 20) + '-' + s.slice(20, 32);
  }
  function post(path, body) {
    /* Picker A/B arm "all": swaps=all where the route admits it (assets/sts-ab.js). */
    var A = STS.ab, u = proxy() + path + '?nonce=' + nonce();
    return w.fetch(A ? A.swaps(u, 'POST') : u, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (b) { return { ok: r.ok, status: r.status, body: b || {} }; });
    }, function () { return { ok: false, status: 0, body: { error: 'NETWORK' } }; });
  }
  function fail(res) {
    var b = res.body || {}, e = b.error;
    if (e === 'LOGIN_REQUIRED' && STS.auth && STS.auth.open) { STS.auth.open('signin', { returnTo: w.location.pathname + w.location.search }); return; }
    if (e === 'NOT_APPROVED') return notice('Not added to your routine', 'Dr. Baumann has not approved this product for your Baumann Skin Type.');
    /* A travel size the page did not catch: the buy box's own line, no popup. */
    if (e === 'INVALID_VARIANT' && b.reason === 'not_retail' && d.querySelector('[data-sts-rt-note]')) { travelNote(true); return; }
    if (e === 'INVALID_VARIANT') return notice('Not added to your routine', (WHY[b.reason] || 'We could not use that size.') + ' Your routine is unchanged.');
    /* Only when the extra-step retry itself was refused (an API without mode
       extra answers 400 field mode): the shopper hears why, nothing faked. */
    if (e === 'NO_MATCHING_STEP' || (e === 'INVALID_BODY' && b.field === 'mode') || e === 'FEATURE_DISABLED') {
      return notice('Not added to your routine', 'This product is not one of your routine’s steps, and we could not add it as an extra step just now. Your routine is unchanged.');
    }
    notice('Your routine did not update', 'Try again in a moment.');
  }
  /* The period to ask for: the matching step's, else the step of the same
     kind (an extra wash sits with the morning cleanser), else morning. */
  function pickTime(r, p) {
    var hit = r ? matches(r.steps, p.slots) : [];
    if (!hit.length && r) hit = matches(r.steps, p.slots, true);
    var e = hit.filter(empty)[0] || hit[0];
    return e ? tod(e) : 'AM';
  }

  function viaServer(p, before) {
    var time = pickTime(before, p), first = time, retried = false;
    function send(body, old) {
      return post('/routine/add', body).then(function (res) {
        var b = res.body || {};
        if (res.status === 422 && b.error === 'NO_MATCHING_STEP' && b.other_time && !retried) {
          retried = true;
          time = b.other_time;
          return send(Object.assign({}, body, { time: time }), old);
        }
        /* No step of the routine takes it: it goes in as an extra step
           (prototype v6.2 addToRoutine, key 'addon'), in the period asked. */
        if (res.status === 422 && b.error === 'NO_MATCHING_STEP' && body.mode !== 'extra') {
          return send(Object.assign({}, body, { mode: 'extra', time: first }), old);
        }
        if (res.status === 409 && b.error === 'STEP_FILLED' && b.conflict) return conflict(b.conflict, body);
        if (!res.ok || !b.steps) { fail(res); return null; }
        var ap = b.applied || {}, kind;
        if (b.result === 'already_in_routine') kind = 'already';
        else if (b.result === 'swapped') kind = 'swap';
        else if (b.result === 'extra_added') kind = ap.stepId ? 'both' : 'addon';
        else kind = 'add';
        var ids = kind === 'both' || kind === 'addon' ? [ap.extraId] : (ap.stepIds || [ap.stepId]);
        var after = clone(b);
        save(after);
        var step = after.steps.filter(function (s) { return s.stepId === ap.stepId; })[0] || null;
        return { kind: kind, ids: ids.filter(Boolean), step: step, after: after, old: old || null, scope: body.scope };
      });
    }
    function conflict(c, body) {
      var occ = c.occurrences || [];
      var both = occ.some(function (o) { return o.time === 'PM'; }) && occ.some(function (o) { return o.time === 'AM'; });
      return askReplace(c.step, c.current_product || {}, p, both).then(function (v) {
        if (v === 'both') return send(Object.assign({}, body, { mode: 'both' }));
        if (v !== 'swap') return null;
        return askScope(c.current_product, p, occ, c.step.time).then(function (sc) {
          if (!sc) return null;
          return send(Object.assign({}, body, { mode: 'swap', scope: sc }), c.current_product || null);
        });
      });
    }
    return send({ variantId: Number(p.variantId) || p.variantId, time: time, mode: 'fill' });
  }

  function viaLocal(p, before) {
    var r = clone(before);
    var hit = matches(r.steps, p.slots);
    if (!hit.length) {
      var near = matches(r.steps, p.slots, true)[0];
      var ex = extraOf(p, null, near ? tod(near) : null);
      r.extras.push(ex);
      return Promise.resolve({ kind: 'addon', ids: [ex.extraId], step: null, after: r });
    }
    var e = hit.filter(empty)[0];
    if (e) {
      var i = r.steps.indexOf(e);
      r.steps[i] = asStep(p, e);
      return Promise.resolve({ kind: 'add', ids: [key(e)], step: r.steps[i], after: r });
    }
    var f = hit[0];
    var occ = r.steps.filter(function (s) { return !empty(s) && vid(s.dtcVariantId) === vid(f.dtcVariantId); });
    var both = occ.length > 1 && occ.some(function (s) { return tod(s) !== tod(f); });
    var curP = { brand: f.brand, title: f.title, displayTitle: f.displayTitle, image: f.image, price: f.priceMsrp, variantId: f.dtcVariantId };
    return askReplace(f, curP, p, both).then(function (v) {
      if (v === 'both') {
        var x = extraOf(p, f, tod(f));
        r.extras.push(x);
        return { kind: 'both', ids: [x.extraId], step: f, after: r };
      }
      if (v !== 'swap') return null;
      var o = occ.map(function (s) { return { stepId: key(s), time: tod(s), slot: s.slot, label: label(s), fits: true }; });
      return askScope(curP, p, o, tod(f)).then(function (sc) {
        if (!sc) return null;
        var list = sc === 'all' ? occ : [f], fi = r.steps.indexOf(f);
        list.forEach(function (s) { r.steps[r.steps.indexOf(s)] = asStep(p, s); });
        return { kind: 'swap', ids: list.map(key), step: r.steps[fi], after: r, old: curP, scope: sc };
      });
    });
  }

  function add(p, btn) {
    if (busy || !p) return Promise.resolve(null);
    if (p.nonRetail) { travelNote(true); return Promise.resolve(null); }
    busy = true;
    if (btn) btn.setAttribute('data-busy', 'true');
    return load().then(function (before) {
      if (before && inRoutine(p, before)) { openWidget([], ''); return null; }
      var m = mode();
      if (m !== 'server' && !before) {
        notice('We could not find your routine', 'Open your Skin Profile to see it, or take the quiz to build one.');
        return null;
      }
      var snap = before ? clone(before) : null;
      return (m === 'server' ? viaServer(p, before) : viaLocal(p, before)).then(function (out) {
        if (!out) return null;
        if (out.kind === 'already') { paint(); openWidget([], ''); return null; }
        if (m !== 'server') save(out.after);
        done(p, out, snap);
        return out;
      });
    }).catch(function () { notice('Your routine did not update', 'Try again in a moment.'); })
      .then(function (v) { busy = false; if (btn) btn.removeAttribute('data-busy'); return v; });
  }

  function openWidget(ids, badge) {
    var W = STS.routineWidget;
    if (W && typeof W.open === 'function') {
      try { W.open({ changedStepIds: ids || [], badge: badge || null, steps: cur ? flat(cur) : undefined }); } catch (e) { /* widget's own */ }
      return true;
    }
    return false;
  }
  /* "✓ In your routine" / See my routine: the widget, else the routine on this
     page (the Skin Profile), else the Skin Profile. */
  function seeRoutine() {
    if (openWidget([], '')) return;
    var h = d.getElementById('sts-profile-routine-h');
    if (h && STS.jump) { STS.jump(h.closest('.sts-profile__routine') || h, { focus: h }); return; }
    w.location.href = STS.routineUrl ? STS.routineUrl() : CFG.profile || '/pages/skin-profile'; /* v6.1 1.1's one rule */
  }
  /* The Skin Profile's rows follow the change (its apply seam), quantities kept. */
  function push() {
    var R = STS.routine;
    if (!cur || !live()) return;
    var q = function (s) { return Object.assign({}, s, { quantity: s.quantity || s.qty || 1 }); };
    try { R.apply({ steps: cur.steps.map(q), extras: cur.extras.map(q), routineSize: R.size ? R.size() : null }); } catch (e) { /* the profile's own */ }
  }
  /* Phones: v6 opens the widget after an add here too (rwOpen on product /
     collection), as a bottom sheet up to 62vh above the bottom menu. Scroll
     the page first so the confirmation card sits wholly above the sheet's
     top edge, where See my routine and Undo stay tappable. */
  function lift(then) {
    var box = d.querySelector('[data-sts-rt-done]');
    var ph = false;
    try { ph = w.matchMedia('(max-width: 860px)').matches; } catch (e) { ph = false; }
    if (!ph || !box || box.hidden) { then(); return; }
    setTimeout(function () {
      var probe = mk('div');
      probe.style.cssText = 'position:fixed;visibility:hidden;height:var(--sts-bottom-offset,0px)';
      d.body.appendChild(probe);
      var off = probe.offsetHeight || 0;
      d.body.removeChild(probe);
      var h = w.innerHeight;
      var dy = box.getBoundingClientRect().bottom - (h - off - Math.round(h * 0.62) - 12);
      var rm = false;
      try { rm = w.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e2) { rm = false; }
      if (dy > 0) w.scrollBy({ top: dy, behavior: rm ? 'auto' : 'smooth' });
      then();
    }, 80);
  }
  function widgetSync() {
    var W = STS.routineWidget;
    if (cur && W && typeof W.refresh === 'function') { try { W.refresh(flat(cur)); } catch (e) { /* widget's own */ } }
  }

  /* The routine as it was, rebuilt from the answer: the same step ids and
     labels on both sides, so the change list shows only this change. */
  function beforeOf(out) {
    var r = clone(out.after), o = out.old;
    if (out.kind === 'both' || out.kind === 'addon') {
      r.extras = r.extras.filter(function (e) { return out.ids.indexOf(e.extraId) < 0; });
      return r;
    }
    r.steps = r.steps.map(function (s) {
      if (out.ids.indexOf(key(s)) < 0) return s;
      return Object.assign({}, s, out.kind === 'swap' && o ? {
        dtcVariantId: o.variantId, productId: o.productId, handle: o.handle, title: o.title, displayTitle: o.displayTitle || o.title, brand: o.brand,
        image: o.image, priceMsrp: o.price != null ? o.price : o.priceMsrp
      } : { dtcVariantId: null });
    });
    return r;
  }

  /* The Skin Profile's Routine history (session only, v6 §7) lists a change
     made on another page: sessionStorage sts.hist_add, read once by
     assets/sts-routine-editor.js, with `b`, the routine right before it in the
     editor's snapshot shape, so it gets Restore like the editor's own. On the
     Skin Profile the editor records the change itself. Returns the entry's
     time, so Undo can take it back. */
  var HADD = 'sts.hist_add';
  function hOwner() { try { return 'c' + ((w.ShopifyAnalytics && w.ShopifyAnalytics.meta.page.customerId) || (w.__st && w.__st.cid) || ''); } catch (e) { return 'c'; } }
  function histAdd(r, prev, after, was) {
    var RC = STS.routineChanged, c = [], q = {}, O = STS.orderRoutine;
    try { c = RC && RC.diff ? RC.diff(flat(prev), flat(after)) : []; } catch (e) { c = []; }
    if (!c.length) return null;
    if (was) {
      Object.keys(was.quantities || {}).forEach(function (k) { if (was.quantities[k] > 1) q[vid(k)] = +was.quantities[k]; });
      was.steps.concat(was.extras).forEach(function (s) { if (s && s.quantity > 1) q[vid(s.dtcVariantId)] = +s.quantity; });
    }
    var st = was ? was.steps.map(function (s) { var x = Object.assign({}, s, { rm: s.included === false ? 1 : 0, hd: 0 }); delete x.swapCandidates; return x; }) : null;
    var e = { o: hOwner(), r: r, at: Date.now(), f: type(), to: type(),
      c: c.map(function (x) { return { step: x.step, from: x.from, to: x.to }; }),
      b: st ? { t: type(), st: O ? O(st, { once: true }) : st, ex: was.extras.slice(), q: q, sz: was.routineSize || null } : null };
    var l = parse(get('sessionStorage', HADD));
    put('sessionStorage', HADD, JSON.stringify((Array.isArray(l) ? l : []).concat([e]).slice(-30)));
    return e.at;
  }

  function done(p, out, snap) {
    var reason = out.kind === 'swap' ? 'swap' : 'add';
    var before = beforeOf(out);
    var H = STS.routineHistory;
    if (H && typeof H.record === 'function') { try { H.record(out.kind === 'swap' ? 'pdp_swap' : 'pdp_add'); } catch (e) { /* history's own */ } }
    last = { p: p, out: out, before: snap, mode: mode(), h: live() ? null : histAdd(reason, before, out.after, snap) };
    push();
    paint();
    emit('sts:routine:updated', { steps: cur.steps, extras: cur.extras, changedStepIds: out.ids, kind: out.kind });
    widgetSync();
    var badge = out.kind === 'swap' ? 'Just updated' : 'Just added';
    var go = function () { lift(function () { openWidget(out.ids, badge); }); };
    /* PDP-2 / prototype changeOpen: "Your routine changed", then the widget;
       for a subscriber show() fires sts:routine:changed (v6 5.3 consent). */
    var RC = STS.routineChanged, queued = false;
    if (RC && RC.show) {
      try { queued = !!RC.show({ reason: reason, before: flat(before), after: flat(out.after), ack: false, glow: false, scroll: false, onClose: go }); } catch (e) { queued = false; }
    }
    if (!queued) go();
  }

  /* Undo: the reverse of the change just made (the routine as it was for a
     local routine; the matching remove / swap-back call for a saved one). */
  function undo() {
    var L = last;
    if (!L || busy) return Promise.resolve(null);
    busy = true;
    var job;
    if (L.mode !== 'server') {
      save(L.before);
      job = Promise.resolve(true);
    } else {
      var o = L.out, id = o.ids[0];
      var req = o.kind === 'both' || o.kind === 'addon' ? post('/routine/extras/' + encodeURIComponent(id) + '/remove')
        : o.kind === 'swap' && o.old ? post('/routine/steps/' + encodeURIComponent(id) + '/swap', { variantId: Number(o.old.variantId) || o.old.variantId, scope: o.ids.length > 1 ? 'all' : 'step' })
        : post('/routine/steps/' + encodeURIComponent(id) + '/remove');
      job = req.then(function (res) {
        if (!res.ok || !res.body || !res.body.steps) { fail(res); return null; }
        save(clone(res.body));
        return true;
      });
    }
    return job.then(function (ok) {
      if (!ok) return null;
      last = null;
      /* Undone before the Skin Profile read it: no history line for it. */
      var hl = L.h && parse(get('sessionStorage', HADD));
      if (Array.isArray(hl)) put('sessionStorage', HADD, JSON.stringify(hl.filter(function (x) { return x && x.at !== L.h; })));
      push();
      paint();
      emit('sts:routine:updated', { steps: cur ? cur.steps : [], extras: cur ? cur.extras : [], changedStepIds: [], kind: 'undo' });
      widgetSync();
      return true;
    }).catch(function () { notice('Your routine did not update', 'Try again in a moment.'); })
      .then(function (v) { busy = false; return v; });
  }

  /* The widget's × on an extra step: the account's (POST
     /routine/extras/:key/remove), else the device / visit copy here. */
  function removeExtra(id) {
    id = str(id);
    if (!id || busy) return Promise.resolve(false);
    busy = true;
    return load().then(function (r) {
      if (!r || !r.extras.some(function (e) { return str(e.extraId) === id; })) return false;
      var m = mode();
      var job = m === 'server' && !/^extra:local-/.test(id)
        ? post('/routine/extras/' + encodeURIComponent(id) + '/remove').then(function (res) {
          if (!res.ok || !res.body || !res.body.steps) { fail(res); return null; }
          return clone(res.body);
        })
        : Promise.resolve(Object.assign(clone(r), { extras: r.extras.filter(function (e) { return str(e.extraId) !== id; }) }));
      return job.then(function (after) {
        if (!after) return false;
        save(after);
        if (last && last.out && last.out.ids.indexOf(id) > -1) last = null;
        push();
        paint();
        emit('sts:routine:updated', { steps: cur.steps, extras: cur.extras, changedStepIds: [], kind: 'remove' });
        widgetSync();
        return true;
      });
    }).catch(function () { notice('Your routine did not update', 'Try again in a moment.'); return false; })
      .then(function (v) { busy = false; return v; });
  }

  /* ---- the products on this page ------------------------------------------ */
  function words(s) { return str(s).split(/\s+/).filter(Boolean).map(function (x) { return x.toUpperCase(); }); }
  /* The SELECTED variant (v6.1 §5.1): its id, its price, and its name with
     the variant in displayTitle, "UV Clear SPF 46 (1.7 oz.)", the same field
     the API sends (#1055, `title` stays the product's), rebuilt when the
     shopper picks another (sts:pdp:variant from assets/sts-product.js).
     nonRetail: a travel / sample size, which stays out of routines. */
  function pdp() {
    var root = d.querySelector('[data-sts-product]');
    if (!root) return null;
    var input = root.querySelector('[data-sts-variant-input]');
    var id = vid(input && input.value);
    if (root.__stsRt && root.__stsRt.variantId === id) return root.__stsRt;
    var vs = root.__stsVs || (root.__stsVs = parse((root.querySelector('[data-sts-variants]') || {}).textContent) || []);
    var v = vs.filter(function (x) { return vid(x.id) === id; })[0] || vs[0] || {};
    var img = root.querySelector('.sts-sticky__img,.sts-pdp__media img');
    var t = root.getAttribute('data-title'), vt = str(v.title), named = vs.length > 1 && vt && vt !== 'Default Title';
    id = id || vid(v.id);
    root.__stsRt = {
      variantId: id, variants: [id],
      productId: root.getAttribute('data-product-id'), handle: root.getAttribute('data-handle'),
      title: t, displayTitle: named ? t + ' (' + vt + ')' : t, variantTitle: named ? vt : null,
      nonRetail: NONRETAIL.test(vt), brand: root.getAttribute('data-vendor'),
      price: v.price != null ? v.price / 100 : null, image: img ? img.currentSrc || img.src : null,
      slots: words(root.getAttribute('data-sts-slots'))
    };
    return root.__stsRt;
  }
  /* v6.1 1.5b: a card is the shared [data-sts-card], or any [data-sts-rt-host]
     (the Skin Profile upsells, the article callout) carrying the same data-*
     plus data-title / data-image, whose link goes right after its
     [data-sts-rt-slot] (the host's own Add to cart). */
  var HOST = '[data-sts-card],[data-sts-rt-host]';
  function card(el) {
    if (el.__stsRt) return el.__stsRt;
    var a = el.querySelector('.sts-card__title a'), img = el.querySelector('.sts-card__media img,.sts-card__imglink img');
    var v = vid(el.getAttribute('data-variant-id'));
    el.__stsRt = {
      variantId: v, variants: [v], productId: el.getAttribute('data-product-id'), handle: el.getAttribute('data-handle'),
      title: el.getAttribute('data-title') || (a ? a.textContent.trim() : ''),
      displayTitle: el.getAttribute('data-display-title') || null, brand: el.getAttribute('data-vendor'),
      price: (parseFloat(el.getAttribute('data-sts-price')) || 0) / 100 || null,
      image: el.getAttribute('data-image') || (img ? img.currentSrc || img.src : null),
      slots: words(el.getAttribute('data-sts-slots'))
    };
    return el.__stsRt;
  }
  /* A card is "right for you" as sts-collection.css decides it: the server's
     verdict when it had one, else the type against the approval list, less a
     missing sensitivity subtype (04-Q7). */
  function subs() {
    var raw = parse(get('localStorage', 'customerSensitivityList'));
    var list = Array.isArray(raw) ? raw : str(raw).split(',');
    return list.map(function (s) { return str(s).trim().toUpperCase(); }).filter(function (s) { return /^S[1-4]$/.test(s); });
  }
  function approved(el) {
    var f = el.getAttribute('data-sts-fit'), t = type();
    if (f === 'approved') return true;
    if (f !== 'unknown' || !t) return false;
    if (words(el.getAttribute('data-sts-approved')).indexOf(t) < 0) return false;
    var sens = words(el.getAttribute('data-sts-sens'));
    return subs().every(function (s) { return sens.indexOf(s) > -1; });
  }

  /* ---- painting ----------------------------------------------------------- */
  function cards() {
    var t = type();
    if (!t || d.querySelector('[data-sts-product]')) return;
    qsa(d, HOST).forEach(function (el) {
      var host = el.hasAttribute('data-sts-rt-host'), slot = el.querySelector('[data-sts-rt-slot]');
      var body = el.querySelector('.sts-card__body');
      if (el.__stsRtT === t || el.querySelector('.sts-card__rt')) return;
      var ok = approved(el);
      /* A host has no row to line up with: only the approved one gets a link. */
      if (host ? !slot || !ok : !body) { el.__stsRtT = t; return; }
      var b = mk('button', 'sts-card__rt', '+ Add to my routine');
      b.type = 'button';
      if (!ok) {
        /* Same height on every card, so a row's Add to cart buttons line up. */
        b.setAttribute('aria-hidden', 'true');
        b.tabIndex = -1;
        b.disabled = true;
      } else {
        b.setAttribute('data-sts-rt-card', '');
        b.addEventListener('click', function () {
          var p = card(el);
          if (inRoutine(p)) { seeRoutine(); return; }
          add(p, b);
        });
      }
      if (slot) slot.parentNode.insertBefore(b, slot.nextSibling); else body.appendChild(b);
    });
  }
  function say(b, t) { if (b.textContent !== t) b.textContent = t; }
  /* The buy box's travel-size line (snippets/sts-pdp-buybox.liquid). */
  function travelNote(show) {
    var n = d.querySelector('[data-sts-rt-note]');
    if (n) { n.textContent = TRAVEL; n.hidden = !show; }
  }
  function paint() {
    cards();
    var P = pdp(), off = false;
    qsa(d, '[data-sts-rt-add]').forEach(function (b) {
      var on = !!P && inRoutine(P);
      say(b, on ? '✓ In your routine' : '+ Add to my routine');
      b.toggleAttribute('data-in', on);
      /* A travel size (buy box chips or the bar's bubble): no add, the line says why. */
      off = !!P && P.nonRetail && !on;
      b.disabled = off;
    });
    travelNote(off);
    qsa(d, '[data-sts-rt-card]').forEach(function (b) {
      var on = inRoutine(card(b.closest(HOST)));
      say(b, on ? '✓ In your routine' : '+ Add to my routine');
      b.toggleAttribute('data-in', on);
    });
    var box = d.querySelector('[data-sts-rt-done]');
    if (!box) return;
    var L = last, show = !!(L && P && L.p.variantId === P.variantId && inRoutine(P));
    if (box.__stsL === (show ? L : null)) return;
    box.__stsL = show ? L : null;
    box.hidden = !show;
    box.textContent = '';
    if (!show) return;
    var o = L.out, s = o.step, per = s && tod(s) === 'PM' ? 'evening' : 'morning', lab = s ? label(s).toLowerCase() : '';
    var title = o.kind === 'swap' ? 'Swapped into your routine' : o.kind === 'both' ? 'Added as an extra step' : 'Added to your routine';
    /* v6.1 1.2 / 1.3 (prototype L3671): the short line, behind a Details pill. */
    var line = o.kind === 'addon' ? 'Now in Extra steps.'
      : !s ? ''
      : o.kind === 'swap' ? (o.old ? 'Replaced ' + nm(o.old) + '.' : 'Now your ' + per + ' ' + lab + '.')
      : o.kind === 'both' ? 'Both are in your ' + per + ' ' + lab + ' step.'
      : 'Now ' + per + ' step ' + stepNo(s) + '.';
    box.appendChild(mk('span', 'sts-rt-done__ok', '✓')).setAttribute('aria-hidden', 'true');
    var t = box.appendChild(mk('div', 'sts-rt-done__text'));
    var h = t.appendChild(mk('p', 'sts-rt-done__title', title));
    if (line) {
      var ln = t.appendChild(mk('p', 'sts-rt-done__line', line));
      if (STS.popups && STS.popups.tuck) STS.popups.tuck(ln, 'p', h);
    }
    var acts = t.appendChild(mk('div', 'sts-rt-done__acts'));
    var see = acts.appendChild(mk('button', 'sts-rt-done__see', 'See my routine'));
    see.type = 'button';
    see.addEventListener('click', function () { if (!openWidget([], '')) w.location.href = CFG.profile || '/pages/skin-profile'; });
    var un = acts.appendChild(mk('button', 'sts-rt-done__undo', 'Undo'));
    un.type = 'button';
    un.addEventListener('click', function () { undo(); });
  }
  function stepNo(s) {
    var O = STS.orderRoutine, list = O && cur ? O(cur.steps.filter(function (x) { return !empty(x) || x === s; })) : [];
    for (var i = 0; i < list.length; i++) { if (key(list[i]) === key(s) && list[i].half === tod(s)) return list[i].n; }
    return s.n || '';
  }

  function boot() {
    qsa(d, '[data-sts-rt-add]').forEach(function (b) {
      if (b.__stsRt) return;
      b.__stsRt = 1;
      b.addEventListener('click', function () {
        var P = pdp();
        if (P && inRoutine(P)) { if (!openWidget([], '')) w.location.href = CFG.profile || '/pages/skin-profile'; return; }
        /* v7.1 §2 (prototype pdRoutine -> smallGuard): the smaller-size
           pop-up guards the routine link as it does Add to cart; OK adds,
           Nevermind adds nothing. Without the guard it just adds. */
        var S = STS.smallSize, root = d.querySelector('[data-sts-product]');
        if (S && S.guard && P && root) S.guard(root, P.variantId, function () { add(P, b); }); else add(P, b);
      });
    });
    paint();
    /* The widget reads the device / account routine on its own; a visit-only
       change or an extra step lives here, so it is handed over once both are
       up (and again after the widget's own first read). */
    if (type()) load().then(function (r) {
      paint();
      if (r && (r.extras.length || mode() === 'visit')) { widgetSync(); setTimeout(widgetSync, 1500); }
      /* A visit-only change outlives a reload here only: the homepage glance
         tiles (sts-home.js) redraw from it. */
      if (r && r.visit) emit('sts:routine:updated', { steps: r.steps, extras: r.extras, changedStepIds: [], kind: 'restore' });
    });
  }

  /* match / stepWords: the product -> step rule and the step's name, shared
     with the routine widget's "This product fits your ... step" note. */
  STS.addRoutine = { get: load, add: add, undo: undo, paint: paint, inRoutine: function (p) { return inRoutine(p); },
    removeExtra: removeExtra, match: function (steps, slots) { return matches(steps, slots); }, stepWords: stepWords };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot); else boot();
  /* Filters, sort and Load more redraw the grid; new cards get their link. */
  var pending = false;
  if (w.MutationObserver && !d.querySelector('[data-sts-product]')) {
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      (w.requestAnimationFrame || setTimeout)(function () {
        pending = false;
        if (type() && qsa(d, HOST).some(function (el) { return el.__stsRtT !== type() && !el.querySelector('.sts-card__rt'); })) paint();
      });
    }).observe(d.documentElement, { childList: true, subtree: true });
  }
  if (STS.onState) STS.onState(function () { cur = null; fallback = 0; boot(); });
  /* Another size picked: only that exact variant reads "✓ In your routine". */
  d.addEventListener('sts:pdp:variant', function () { paint(); });
  /* The routine changed elsewhere: Keep my subscription as it was put it back
     (sts-consent.js, {doc} = the saved routine now; the change this page made
     is gone, so is its Undo), or the Skin Profile saved {steps}. The buttons
     follow it without a reload. */
  d.addEventListener('sts:routine:reverted', function (e) {
    var doc = e && e.detail && e.detail.doc;
    if (!doc || !Array.isArray(doc.steps)) return;
    last = null;
    save(clone(doc));
    paint();
  });
  d.addEventListener('sts:routine:change', function (e) {
    var s = e && e.detail && e.detail.steps;
    if (!Array.isArray(s) || !s.length) return;
    cur = clone({ steps: s, extras: cur ? cur.extras : [] });
    paint();
  });
})(window, document);
